import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { MusicSheetService, MusicSheet } from '../../services/musicSheetService';
import PageHeader from '../common/PageHeader';
import './MusicSheetEditor.css';
import abcjs from 'abcjs';

interface MusicSheetEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type EditorMode = 'text' | 'visual';
type TabType = 'content' | 'info';

const MusicSheetEditor: React.FC<MusicSheetEditorProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const { sheetId } = useParams<{ sheetId: string }>();
  const navigate = useNavigate();
  const isNewSheet = sheetId === 'new';

  const [sheet, setSheet] = useState<MusicSheet | null>(null);
  const [sheetName, setSheetName] = useState('');
  const [description, setDescription] = useState('');
  const [abcNotation, setAbcNotation] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('content');
  const [editorMode, setEditorMode] = useState<EditorMode>('text');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const abcContainerRef = useRef<HTMLDivElement>(null);
  const synthControlRef = useRef<any>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load sheet data if editing existing sheet
  useEffect(() => {
    if (!isNewSheet && sheetId) {
      loadSheet();
    } else {
      // Initialize blank sheet
      initializeBlankSheet();
    }
  }, [sheetId, isNewSheet]);

  // Render ABC notation when switching to visual mode or when notation changes
  useEffect(() => {
    if (editorMode === 'visual' && abcNotation && abcContainerRef.current) {
      renderABCNotation(abcNotation);
    }
  }, [editorMode, abcNotation]);

  const loadSheet = async () => {
    if (!sheetId) return;

    try {
      const loadedSheet = await MusicSheetService.getSheetById(sheetId);
      setSheet(loadedSheet);
      setSheetName(loadedSheet.sheet_name);
      setDescription(loadedSheet.description || '');
      setAbcNotation(loadedSheet.abc_notation);
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading sheet:', err);
      setError('Failed to load sheet');
    }
  };

  const initializeBlankSheet = () => {
    // Basic ABC notation template
    const blankABC = `X:1
T:Untitled
M:4/4
L:1/8
K:C
CDEF GABc|]`;
    
    setSheet(null);
    setSheetName('');
    setDescription('');
    setAbcNotation(blankABC);
    setHasChanges(false);
  };

  const renderABCNotation = (notation: string) => {
    if (!abcContainerRef.current) return;

    try {
      // Clear previous content
      abcContainerRef.current.innerHTML = '';

      // Render the ABC notation
      abcjs.renderAbc(abcContainerRef.current, notation, {
        responsive: 'resize',
        scale: 1.0
      });
    } catch (err) {
      console.error('Error rendering ABC notation:', err);
      setError('Failed to render music notation');
    }
  };

  const handleNotationChange = (value: string) => {
    setAbcNotation(value);
    setHasChanges(true);
  };

  const handleNameChange = (value: string) => {
    setSheetName(value);
    setHasChanges(true);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!sheetName.trim()) {
      setError('Sheet name is required');
      return;
    }

    if (!abcNotation.trim()) {
      setError('ABC notation is required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      if (isNewSheet) {
        // Create new sheet
        const newSheet = await MusicSheetService.saveMusicSheet({
          sheet_name: sheetName,
          description: description || undefined,
          abc_notation: abcNotation
        });
        // Navigate to edit mode for the new sheet
        navigate(`/music-sheets/editor/${newSheet.sheet_id}`, { replace: true });
        setSheet(newSheet);
      } else if (sheetId) {
        // Update existing sheet
        const updatedSheet = await MusicSheetService.updateSheet(sheetId, {
          sheet_name: sheetName,
          description: description || undefined,
          abc_notation: abcNotation
        });
        setSheet(updatedSheet);
      }

      setHasChanges(false);
      setIsSaving(false);
    } catch (err) {
      console.error('Error saving sheet:', err);
      setError(err instanceof Error ? err.message : 'Failed to save sheet');
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!sheetId || isNewSheet) return;

    if (!window.confirm('Are you sure you want to delete this sheet?')) {
      return;
    }

    try {
      await MusicSheetService.deleteSheet(sheetId);
      navigate('/music-sheets');
    } catch (err) {
      console.error('Error deleting sheet:', err);
      setError('Failed to delete sheet');
    }
  };

  const handleCancel = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return;
    }
    navigate('/music-sheets');
  };

  const handlePlayPause = async () => {
    if (!abcNotation) return;

    try {
      if (isPlaying) {
        // Stop playback
        if (synthControlRef.current) {
          synthControlRef.current.pause();
        }
        setIsPlaying(false);
      } else {
        // Start playback
        if (abcjs.synth.supportsAudio()) {
          const visualObj = abcjs.renderAbc('*', abcNotation)[0];
          
          if (!synthControlRef.current) {
            synthControlRef.current = new abcjs.synth.CreateSynth();
            await synthControlRef.current.init({
              visualObj: visualObj
            });
          }

          await synthControlRef.current.prime();
          synthControlRef.current.start();
          setIsPlaying(true);

          // Listen for when playback ends
          synthControlRef.current.onEnded = () => {
            setIsPlaying(false);
          };
        } else {
          setError('Audio playback is not supported in your browser');
        }
      }
    } catch (err) {
      console.error('Error playing music:', err);
      setError('Failed to play music');
      setIsPlaying(false);
    }
  };

  return (
    <div className="music-sheet-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          <button 
            className="page-header-icon-btn"
            onClick={handleCancel}
            aria-label="Back"
          >
            ←
          </button>
        }
        centerContent={
          <h1 className="page-header-center-title">
            {isNewSheet ? 'Create New Song' : 'Edit Song'}
          </h1>
        }
        rightActions={
          <div className="music-sheet-editor-header-actions">
            <button
              className={`music-sheet-editor-play-btn ${isPlaying ? 'playing' : ''}`}
              onClick={handlePlayPause}
              disabled={!abcNotation.trim()}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            {!isNewSheet && sheetId && (
              <button
                className="music-sheet-editor-delete-btn"
                onClick={handleDelete}
                disabled={isSaving}
              >
                Delete
              </button>
            )}
            <button
              className="music-sheet-editor-save-btn"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="music-sheet-editor-content">
        {/* Error Message */}
        {error && (
          <div className="music-sheet-editor-error">
            <p>{error}</p>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="music-sheet-editor-tabs">
          <button
            className={`music-sheet-editor-tab ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            🎵 Song Content
          </button>
          <button
            className={`music-sheet-editor-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            📝 Song Info
          </button>
        </div>

        {/* Tab Content */}
        <div className="music-sheet-editor-tab-content">
          {activeTab === 'content' ? (
            <>
              {/* Editor Mode Toggle */}
              <div className="music-sheet-editor-mode-toggle">
                <button
                  className={`music-sheet-editor-mode-btn ${editorMode === 'text' ? 'active' : ''}`}
                  onClick={() => setEditorMode('text')}
                >
                  📝 Text Editor
                </button>
                <button
                  className={`music-sheet-editor-mode-btn ${editorMode === 'visual' ? 'active' : ''}`}
                  onClick={() => setEditorMode('visual')}
                >
                  🎼 Visual Preview
                </button>
              </div>

              {/* Editor Content */}
              <div className="music-sheet-editor-main">
                {editorMode === 'text' ? (
                  <div className="music-sheet-editor-text-container">
                    <textarea
                      ref={textareaRef}
                      className="music-sheet-editor-textarea-large"
                      value={abcNotation}
                      onChange={(e) => handleNotationChange(e.target.value)}
                      placeholder="Enter ABC notation here..."
                      spellCheck={false}
                    />
                    <div className="music-sheet-editor-help">
                      <p>Edit ABC notation directly. Changes are saved when you click Save.</p>
                    </div>
                  </div>
                ) : (
                  <div className="music-sheet-editor-visual-container">
                    <div className="music-sheet-editor-visual-display" ref={abcContainerRef}></div>
                    <div className="music-sheet-editor-help">
                      <p>Visual preview of your ABC notation. Switch to Text Editor to make changes.</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="music-sheet-editor-info-tab">
              <div className="music-sheet-editor-metadata">
                <div className="music-sheet-editor-form-group">
                  <label htmlFor="sheet-name" className="music-sheet-editor-label">
                    Song Name *
                  </label>
                  <input
                    id="sheet-name"
                    type="text"
                    className="music-sheet-editor-input"
                    value={sheetName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Enter song name"
                  />
                </div>

                <div className="music-sheet-editor-form-group">
                  <label htmlFor="sheet-description" className="music-sheet-editor-label">
                    Description
                  </label>
                  <textarea
                    id="sheet-description"
                    className="music-sheet-editor-textarea"
                    value={description}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    placeholder="Enter description (optional)"
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MusicSheetEditor;

